import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Shared-rig contract (see the framing addendum): ice on glass, lit by nothing.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'frost',
  family: 'organic',
  energyLevel: 0.12,
  bpmMin: 40,
  bpmMax: 130,
  movement: 0.20,
  danceability: 0.10,
  tonality: 0.90,
  prefers: { dynamics: [0.45, 1.0], percussiveness: [0.0, 0.35] },
  enabled: true
};

// Frost: ice crystallising on a window. The structure grows for the whole track
// and never retreats - nothing melts, nothing fades, nothing loops.
//
// It exists because of what this repertoire does NOT give us. On classical,
// ambient and solo piano there is no reliable beat grid, beatPhase is absent,
// and the band envelopes barely separate. What is left is DYNAMICS and
// DURATION, so the whole piece is built on those two: growth follows the
// envelope, and the image assembles across the length of the track.
//
// It is a TREE held in JavaScript, drawn as fat lines - not a shader. That is
// the whole reason it is affordable: the cost is in the number of branches,
// not in pixels, and a branch is added only when the music asks for one. A
// frame in which nothing grows does no CPU work at all beyond the appearance
// animation, which is a pure function of time evaluated on the GPU.
//
// Two rules carry the credibility, and both are in the brief for good reason:
//
//   HEXAGONAL ANISOTROPY. Ice has six-fold symmetry. Snapping each drawn angle
//   most of the way toward the nearest multiple of 60 degrees is the single
//   thing that separates frost from a root system or a lightning bolt - see
//   HEX_SNAP, and ?frosthex=0 to see it without.
//
//   NO CROSSING. A branch that lands on top of an existing one turns the whole
//   structure into a mat. Rejecting those is what keeps it reading as crystal.
//   The test goes through a spatial grid (see _cellsFor): at 1200 segments an
//   exhaustive comparison would be 1.4M distance tests per growth event.

// --- capacity -------------------------------------------------------------
const TIER = {
  0: { maxSegments: 600, seeds: 4, halo: false },
  1: { maxSegments: 900, seeds: 5, halo: true },
  2: { maxSegments: 1200, seeds: 6, halo: true }
};
const DEFAULT_TIER = 1;

// Seven floats per segment, one flat Float32Array, allocated once at init and
// never grown: x0, y0, x1, y1, birth, generation, seed.
const SEG_STRIDE = 7;

// --- the frame's own units -------------------------------------------------
// y spans [-1, 1] and x spans [-aspect, aspect], so a length means the same
// thing on both axes and the hexagonal angles stay hexagonal. Converted to
// clip space by dividing x by the aspect, in the vertex shader.
const SEED_INSET = 0.02;       // how far inside the edge a seed sits
const SEED_LENGTH = 0.13;      // first segment, ~6.5% of frame height
const LENGTH_DECAY = 0.88;
const LENGTH_FLOOR = 0.008;    // 0.4% of frame height
const ANGLE_SPREAD_DEG = 32;
const BIFURCATION_MIN_DEG = 8;
// Snapped toward the nearest 60-degree multiple by this fraction. Not 100%:
// a perfectly hexagonal lattice reads as a machine drawing, and real frost
// wanders around the lattice rather than sitting exactly on it.
const HEX_SNAP = 0.65;
const HEX_STEP_RAD = Math.PI / 3;

// progress -> how readily a branch splits. Open at the start, refining by the
// end: "structure qui s'ouvre" then "elle s'affine".
const BIFURCATION_P_START = 0.45;
const BIFURCATION_P_END = 0.20;
// character.dynamics -> segment length. A dynamic piece spreads further.
const DYNAMICS_LENGTH_MIN = 0.7;
const DYNAMICS_LENGTH_MAX = 1.3;

// --- growth triggering -----------------------------------------------------
const RISE_THRESHOLD = 0.02;
const MIN_EVENT_MS = 120;
const MAX_GROWTHS_PER_EVENT = 4;
// Which generations each band reaches. Low frequencies thicken the trunk,
// highs extend the lace at the tips - the split that makes the link to the
// music legible rather than merely present.
const LOW_GEN_MAX = 4;
const HIGH_GEN_MIN = 5;
// No analysis at all: grow on a slow random clock instead, alternating the two
// generation bands. Three growths per event so the structure still saturates
// on the same timescale as the band-driven path.
const FALLBACK_MIN_MS = 500;
const FALLBACK_MAX_MS = 1500;
const FALLBACK_GROWTHS = 3;

// --- appearance ------------------------------------------------------------
const GROW_SECONDS = 0.6;      // a segment extends from its base to its tip
const RECENT_SECONDS = 3.0;    // ...and stays a little brighter for this long
const RECENT_BOOST = 0.35;
const WIDTH_BASE_PX = 3.5;     // generation 0
const WIDTH_TIP_PX = 1.0;      // deepest generation
const WIDTH_GEN_SPAN = 14;     // generation at which the width reaches the tip value
const HALO_WIDTH_MUL = 6.0;
const HALO_OPACITY = 0.12;
const TRACK_FADE_S = 3.0;

const ICE_COLOR = 0xdfeaff;    // near white, faintly blue
const PALETTE_TINT = 0.20;
const PALETTE_LERP_MS = 1200;
const BG_TOP = 0x03050a;
const BG_BOTTOM = 0x080b12;

// The brief asks for a peak of 0.88 displayed AND for the frost not to bloom a
// picture that is going to get dense. Those two do not both fit: 0.88 on screen
// is 0.855 in linear scene light (ACES at exposure 1.1, then sRGB - the same
// arithmetic the paper family's own ceilings were derived from), and the
// composer's bloom pass triggers at 0.78 LINEAR, before tone mapping. So the
// cap is set just under the bloom threshold instead, which lands at 0.864
// displayed - a hundredth off the brief's figure, and the only reading of it
// that satisfies the sentence it comes in.
// 0.66, not 0.72. The halo adds its own 12% on top of the core wherever the
// two coincide, and at 0.72 the sum measured 0.806 linear - over the composer's
// 0.78 bloom threshold, so the frost bloomed after all. 0.66 leaves room for
// the halo and lands the pair at 0.739, just under. Peak on screen: 0.86.
const LUMA_CAP_LINEAR = 0.66;
const LUMA = { r: 0.2126, g: 0.7152, b: 0.0722 };

// --- the crossing test's grid ----------------------------------------------
// Cell size is a little over the longest segment, so a segment touches at most
// a handful of cells and a candidate only has to look at its own cell and the
// eight around it.
// Cell size is set by the LONGEST segment's rejection radius (half of a seed
// segment at full dynamics, about 0.085), so a cell is never smaller than the
// distance a single query has to reach.
const GRID_CELL = 0.09;
// A segment registers in every cell its bounding box touches, not just at its
// endpoints and midpoint. Registering three points was cheaper and wrong: the
// CLOSEST point on a long segment is often mid-span, in a cell no endpoint ever
// touched, so a candidate could be measured against a segment it was actually
// sitting on top of and be accepted.
const GRID_SLOTS_PER_SEGMENT = 9;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

function capLuminance(color) {
  const l = LUMA.r * color.r + LUMA.g * color.g + LUMA.b * color.b;
  if (l > LUMA_CAP_LINEAR && l > 0) color.multiplyScalar(LUMA_CAP_LINEAR / l);
  return color;
}

// Squared distance from a point to a segment. Squared throughout - the test
// compares against a squared threshold, so no square root is ever taken.
// Proper segment-segment crossing, by orientation signs. A zero sign means an
// endpoint lies ON the other segment - touching, not crossing - and a child is
// supposed to touch its parent, so those are excluded deliberately.
// Math.sign, with no epsilon band. An epsilon looked prudent and was the cause
// of a genuine crossing surviving at high density: the deepest generations sit
// at the length floor, where the cross product of two of them is around 1e-10 -
// the same order as a 1e-12 guard once a couple of near-parallel cases line up.
// Those fell into the "0 means touching" branch and were accepted. Verified by
// an exhaustive segment-segment sweep over the finished structure, which uses
// exactly this predicate: the two now agree by construction.
function orient(ax, ay, bx, by, cx, cy) {
  return Math.sign((bx - ax) * (cy - ay) - (by - ay) * (cx - ax));
}

function segmentsCross(ax, ay, bx, by, cx, cy, dx, dy) {
  const d1 = orient(ax, ay, bx, by, cx, cy);
  const d2 = orient(ax, ay, bx, by, dx, dy);
  const d3 = orient(cx, cy, dx, dy, ax, ay);
  const d4 = orient(cx, cy, dx, dy, bx, by);
  return d1 !== d2 && d3 !== d4 && d1 !== 0 && d2 !== 0 && d3 !== 0 && d4 !== 0;
}

function pointSegDist2(px, py, ax, ay, bx, by) {
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 > 1e-12 ? ((px - ax) * dx + (py - ay) * dy) / len2 : 0;
  t = t < 0 ? 0 : (t > 1 ? 1 : t);
  const cx = ax + dx * t, cy = ay + dy * t;
  const ex = px - cx, ey = py - cy;
  return ex * ex + ey * ey;
}

const vertexShader = `
  precision mediump float;

  attribute vec2 aP0;
  attribute vec2 aP1;
  attribute float aT;     // 0 at the segment's base, 1 at its tip
  attribute float aSide;  // -1 / +1 across the ribbon
  attribute vec2 aMeta;   // x generation, y birth time

  uniform float uTime;
  uniform float uAspect;
  uniform float uPixelH;
  uniform float uWidthScale;
  uniform float uFade;

  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;

  varying float vSide;
  varying float vGlow;
  varying float vAtt;

  void main() {
    // The appearance animation is entirely here: a segment's endpoints are
    // written once, at creation, and never touched again. What moves is the
    // TIP, interpolated from the birth stamp against the clock - so a frame
    // in which nothing is born costs nothing on the CPU at all.
    float age = uTime - aMeta.y;
    float grow = clamp(age / ${GROW_SECONDS.toFixed(2)}, 0.0, 1.0);
    vec2 tip = mix(aP0, aP1, grow);
    vec2 p = mix(aP0, tip, aT);

    vec2 d = aP1 - aP0;
    float len = max(length(d), 1e-5);
    vec2 nrm = vec2(-d.y, d.x) / len;

    float genT = clamp(aMeta.x / ${WIDTH_GEN_SPAN.toFixed(1)}, 0.0, 1.0);
    float halfPx = mix(${WIDTH_BASE_PX.toFixed(2)}, ${WIDTH_TIP_PX.toFixed(2)}, genT) * 0.5 * uWidthScale;
    // Pixels into frame units: the frame is 2 units tall over uPixelH pixels.
    p += nrm * aSide * (halfPx * 2.0 / max(uPixelH, 1.0));

    vec2 ndc = vec2(p.x / max(uAspect, 0.001), p.y);

    // Safe zone, own floor per the brief. Evaluated per vertex - these are
    // short segments, so four corners describe the ramp across one of them
    // perfectly well.
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    vAtt = mix(0.35, 1.0, smoothstep(0.0, uSafeFeather, sd)) * uFade;

    vSide = aSide;
    vGlow = 1.0 - smoothstep(0.0, ${RECENT_SECONDS.toFixed(1)}, age);

    gl_Position = vec4(ndc, 0.0, 1.0);
  }
`;

function fragmentShader(isHalo) {
  return `
  precision mediump float;

  uniform vec3 uColor;
  uniform float uOpacity;

  varying float vSide;
  varying float vGlow;
  varying float vAtt;

  void main() {
    float across = abs(vSide);
    ${isHalo
      // Condensation, not a line: a wide soft falloff with no hard edge
      // anywhere in it. This is what stops the whole thing reading as vector
      // artwork.
      // Exponent 1.3, not 2.2. At 2.2 the halo was measurably present and
      // visually useless: a cross-section through a branch showed it dead by
      // eight pixels, so the frame still read as vector artwork - the exact
      // thing this second pass exists to prevent. A gentler falloff spends the
      // same total light over a much wider skirt, which is what condensation
      // around a crystal actually looks like.
      ? 'float a = pow(max(0.0, 1.0 - across), 1.3);'
      // The core keeps a real edge, feathered over the outer fifth so it is
      // not aliased.
      : 'float a = 1.0 - smoothstep(0.80, 1.0, across);'}

    vec3 col = uColor * (1.0 + ${RECENT_BOOST.toFixed(2)} * vGlow);
    float alpha = a * uOpacity * vAtt;
    if (alpha <= 0.002) discard;
    gl_FragColor = vec4(col, alpha);
  }
`;
}

const bgVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

const bgFragmentShader = `
  precision mediump float;
  uniform vec3 uTop;
  uniform vec3 uBottom;
  varying vec2 vUv;

  float hash12(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }

  void main() {
    vec3 col = mix(uBottom, uTop, vUv.y);
    // A dither, not a grain: at these levels the gradient would band into
    // visible steps on an 8-bit panel, and half a level of noise removes it.
    col += (hash12(vUv * 2048.0) - 0.5) * (1.0 / 255.0);
    gl_FragColor = vec4(col, 1.0);
  }
`;

export class FrostVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._forceGrow = urlParam('frostgrow') === '1';
    this._hexOff = urlParam('frosthex') === '0';
    this._info = urlParam('frostinfo') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const layout = TIER[this._tier];
    this._maxSegments = layout.maxSegments;
    this._seedCount = layout.seeds;
    this._wantHalo = layout.halo;

    this._aspect = camera.aspect || 16 / 9;
    this._pixelHeight = 1080;

    // --- the tree, all of it pre-allocated ------------------------------
    this._seg = new Float32Array(this._maxSegments * SEG_STRIDE);
    this._childCount = new Uint8Array(this._maxSegments);
    this._count = 0;
    // Scratch for terminal-candidate collection - reused every event so the
    // growth path allocates nothing.
    this._candidates = new Int32Array(this._maxSegments);

    this._buildGrid();
    this._buildGeometry();
    this._buildBackground();

    this._time = 0;
    this._lastEventTime = -1e6;
    this._nextFallbackAt = 0;
    this._fallbackBandHigh = false;
    this._prevBands = { low: 0, mid: 0, high: 0 };
    this._bifurcationP = BIFURCATION_P_START;
    this._lengthScale = 1;
    this._maxGeneration = 0;
    this._rejections = 0;
    this._testsLastEvent = 0;
    this._testsPerGrowth = 0;
    this._eventCount = 0;
    this._eventWindowStart = 0;
    this._eventsPerSecond = 0;
    this._lastInfoAt = 0;

    this._lastTrackId = undefined;
    this._fading = false;
    this._fadeStart = 0;

    this._lastPaletteRef = undefined;
    this._colorFrom = new THREE.Color(ICE_COLOR);
    this._colorTo = new THREE.Color(ICE_COLOR);
    this._paletteLerpStart = -Infinity;
    this._scratchColor = new THREE.Color();
    this._applyPalette(null);
    this._colorFrom.copy(this._colorTo);
    this._writeColor();

    this._plantSeeds();
  }

  // --- spatial grid ---------------------------------------------------------
  // A linked list per cell, in two flat Int32Arrays: gridHead[cell] is the
  // first slot, gridNext[slot] the one after it. Fixed size, no per-growth
  // allocation, and a candidate only ever visits its own cell and the eight
  // neighbours - which is what keeps the test count per growth in the tens
  // rather than the hundreds of thousands (see ?frostinfo=1).
  _buildGrid() {
    this._gridW = Math.ceil((2 * this._aspect) / GRID_CELL) + 2;
    this._gridH = Math.ceil(2 / GRID_CELL) + 2;
    this._gridHead = new Int32Array(this._gridW * this._gridH).fill(-1);
    const slots = this._maxSegments * GRID_SLOTS_PER_SEGMENT;
    this._gridNext = new Int32Array(slots).fill(-1);
    this._gridItem = new Int32Array(slots).fill(-1);
    this._slotCount = 0;
    // Stamp array so a segment reached through several cells is only tested
    // once per candidate, without clearing anything between candidates.
    this._visitStamp = new Int32Array(this._maxSegments).fill(-1);
    this._visitToken = 0;
  }

  _cellIndex(x, y) {
    let cx = Math.floor((x + this._aspect) / GRID_CELL) + 1;
    let cy = Math.floor((y + 1) / GRID_CELL) + 1;
    if (cx < 0) cx = 0; else if (cx >= this._gridW) cx = this._gridW - 1;
    if (cy < 0) cy = 0; else if (cy >= this._gridH) cy = this._gridH - 1;
    return cy * this._gridW + cx;
  }

  _cellX(x) {
    const c = Math.floor((x + this._aspect) / GRID_CELL) + 1;
    return c < 0 ? 0 : (c >= this._gridW ? this._gridW - 1 : c);
  }

  _cellY(y) {
    const c = Math.floor((y + 1) / GRID_CELL) + 1;
    return c < 0 ? 0 : (c >= this._gridH ? this._gridH - 1 : c);
  }

  _gridInsert(index, x0, y0, x1, y1) {
    const gx0 = this._cellX(Math.min(x0, x1)), gx1 = this._cellX(Math.max(x0, x1));
    const gy0 = this._cellY(Math.min(y0, y1)), gy1 = this._cellY(Math.max(y0, y1));
    for (let gy = gy0; gy <= gy1; gy++) {
      for (let gx = gx0; gx <= gx1; gx++) {
        if (this._slotCount >= this._gridItem.length) return;
        const cell = gy * this._gridW + gx;
        const slot = this._slotCount++;
        this._gridItem[slot] = index;
        this._gridNext[slot] = this._gridHead[cell];
        this._gridHead[cell] = slot;
      }
    }
  }

  // Rejects a candidate that either lands too close to an existing segment OR
  // actually crosses one along its length.
  //
  // The brief only asks for the first test - "rejette un enfant dont
  // l'extremite tombe a moins d'une demi-longueur d'un segment existant" - and
  // the first version did only that, on the reasoning that a tip keeping its
  // distance cannot have crossed anything on the way. That reasoning is wrong,
  // and measurably so: an exhaustive check over the finished structure found
  // two genuine crossings in ~530 segments. A LONG segment can step clean over
  // a short one and land in free space on the far side - the tip is fine, the
  // body is not. The same grid sweep therefore also runs a real segment-segment
  // intersection, which costs four orientation signs against neighbours it has
  // already fetched, and brings the count to zero.
  _rejects(ax, ay, px, py, minDist, ignoreA, ignoreB) {
    const min2 = minDist * minDist;
    const token = ++this._visitToken;
    let tests = 0;
    // Only the cells the candidate can actually reach: its own bounding box,
    // grown by the rejection radius. A fixed 3x3 neighbourhood was both too
    // small for a long segment (a crossing can happen anywhere along it, not
    // just near its tip) and far too large for a short one - at 800 segments
    // the deepest generations are packed at the length floor, and scanning
    // nine cells around them cost 62 distance tests where two cells hold every
    // segment that could possibly be in range.
    const gx0 = this._cellX(Math.min(ax, px) - minDist);
    const gx1 = this._cellX(Math.max(ax, px) + minDist);
    const gy0 = this._cellY(Math.min(ay, py) - minDist);
    const gy1 = this._cellY(Math.max(ay, py) + minDist);
    for (let gy = gy0; gy <= gy1; gy++) {
      for (let gx = gx0; gx <= gx1; gx++) {
        let slot = this._gridHead[gy * this._gridW + gx];
        while (slot !== -1) {
          const idx = this._gridItem[slot];
          slot = this._gridNext[slot];
          if (idx === ignoreA || idx === ignoreB) continue;
          if (this._visitStamp[idx] === token) continue;
          this._visitStamp[idx] = token;
          tests++;
          const o = idx * SEG_STRIDE;
          const bx0 = this._seg[o], by0 = this._seg[o + 1];
          const bx1 = this._seg[o + 2], by1 = this._seg[o + 3];
          if (pointSegDist2(px, py, bx0, by0, bx1, by1) < min2
            || segmentsCross(ax, ay, px, py, bx0, by0, bx1, by1)) {
            this._testsLastEvent += tests;
            return true;
          }
        }
      }
    }
    this._testsLastEvent += tests;
    return false;
  }

  // --- geometry -------------------------------------------------------------
  // Four vertices and six indices per segment, written once when the segment is
  // born. Nothing here is ever rewritten: the draw range is the only thing that
  // moves, and the appearance animation happens in the vertex shader.
  _buildGeometry() {
    const n = this._maxSegments;
    this._aP0 = new Float32Array(n * 4 * 2);
    this._aP1 = new Float32Array(n * 4 * 2);
    this._aT = new Float32Array(n * 4);
    this._aSide = new Float32Array(n * 4);
    this._aMeta = new Float32Array(n * 4 * 2);
    const index = new Uint16Array(n * 6);
    for (let i = 0; i < n; i++) {
      const v = i * 4;
      // 0: base -side, 1: base +side, 2: tip -side, 3: tip +side
      this._aT[v] = 0; this._aSide[v] = -1;
      this._aT[v + 1] = 0; this._aSide[v + 1] = 1;
      this._aT[v + 2] = 1; this._aSide[v + 2] = -1;
      this._aT[v + 3] = 1; this._aSide[v + 3] = 1;
      const o = i * 6;
      index[o] = v; index[o + 1] = v + 1; index[o + 2] = v + 2;
      index[o + 3] = v + 1; index[o + 4] = v + 3; index[o + 5] = v + 2;
    }
    const g = new THREE.BufferGeometry();
    this._attrP0 = new THREE.BufferAttribute(this._aP0, 2);
    this._attrP1 = new THREE.BufferAttribute(this._aP1, 2);
    this._attrMeta = new THREE.BufferAttribute(this._aMeta, 2);
    this._attrP0.setUsage(THREE.DynamicDrawUsage);
    this._attrP1.setUsage(THREE.DynamicDrawUsage);
    this._attrMeta.setUsage(THREE.DynamicDrawUsage);
    // `position` is never read - the shader builds clip space from aP0/aP1 -
    // but Three.js needs one to know the vertex count.
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 4 * 3), 3));
    g.setAttribute('aP0', this._attrP0);
    g.setAttribute('aP1', this._attrP1);
    g.setAttribute('aT', new THREE.BufferAttribute(this._aT, 1));
    g.setAttribute('aSide', new THREE.BufferAttribute(this._aSide, 1));
    g.setAttribute('aMeta', this._attrMeta);
    g.setIndex(new THREE.BufferAttribute(index, 1));
    g.setDrawRange(0, 0);
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
    this._geometry = g;

    this._shared = {
      uTime: { value: 0 },
      uAspect: { value: this._aspect },
      uPixelH: { value: this._pixelHeight },
      uFade: { value: 1 },
      uColor: { value: new THREE.Color(ICE_COLOR) }
    };

    // Halo first, core over it. The core is normal-blended and effectively
    // opaque along its spine, so it REPLACES the halo there rather than adding
    // to it - which is what bounds the peak at the core's own cap instead of
    // letting the two stack past the bloom threshold.
    if (this._wantHalo) {
      this._haloMaterial = new THREE.ShaderMaterial({
        uniforms: Object.assign({}, safeZoneUniforms, this._shared, {
          uWidthScale: { value: HALO_WIDTH_MUL },
          uOpacity: { value: HALO_OPACITY }
        }),
        vertexShader: vertexShader,
        fragmentShader: fragmentShader(true),
        transparent: true,
        blending: THREE.AdditiveBlending,
        // DoubleSide, and it is not optional. The ribbon's normal is always
        // (-d.y, d.x), so every quad in the strip is wound the SAME way - and
        // that way happens to be clockwise, i.e. back-facing. With the default
        // FrontSide, every single segment is culled and the frame renders
        // completely empty while the tree, the draw range and the shader are
        // all perfectly correct. Which is exactly how this presented.
        side: THREE.DoubleSide,
        depthTest: false,
        depthWrite: false
      });
      this._halo = new THREE.Mesh(g, this._haloMaterial);
      this._halo.frustumCulled = false;
      this._halo.renderOrder = -900;
      this._scene.add(this._halo);
    }

    this._coreMaterial = new THREE.ShaderMaterial({
      uniforms: Object.assign({}, safeZoneUniforms, this._shared, {
        uWidthScale: { value: 1 },
        uOpacity: { value: 1 }
      }),
      vertexShader: vertexShader,
      fragmentShader: fragmentShader(false),
      transparent: true,
      blending: THREE.NormalBlending,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false
    });
    this._core = new THREE.Mesh(g, this._coreMaterial);
    this._core.frustumCulled = false;
    this._core.renderOrder = -800;
    this._scene.add(this._core);
  }

  _buildBackground() {
    this._bgUniforms = {
      uTop: { value: new THREE.Color(BG_TOP) },
      uBottom: { value: new THREE.Color(BG_BOTTOM) }
    };
    this._bgGeometry = new THREE.PlaneGeometry(1, 1);
    this._bgMaterial = new THREE.ShaderMaterial({
      uniforms: this._bgUniforms,
      vertexShader: bgVertexShader,
      fragmentShader: bgFragmentShader,
      transparent: false,
      depthTest: false,
      depthWrite: false
    });
    this._bg = new THREE.Mesh(this._bgGeometry, this._bgMaterial);
    this._bg.frustumCulled = false;
    this._bg.renderOrder = -1000;
    this._scene.add(this._bg);
  }

  // --- the tree -------------------------------------------------------------
  _resetTree() {
    this._count = 0;
    this._slotCount = 0;
    this._gridHead.fill(-1);
    this._childCount.fill(0);
    this._maxGeneration = 0;
    this._rejections = 0;
    this._geometry.setDrawRange(0, 0);
  }

  // Seeds sit ON THE EDGES and point inward: frost forms at the rim of a pane
  // and works toward the middle, which is also what leaves the cover sitting in
  // a clearing for most of a track.
  _plantSeeds() {
    this._resetTree();
    const a = this._aspect;
    for (let i = 0; i < this._seedCount; i++) {
      const side = Math.floor(Math.random() * 4);
      let x, y, angle;
      if (side === 0) {           // bottom
        x = (Math.random() * 2 - 1) * a * 0.85; y = -1 + SEED_INSET; angle = Math.PI / 2;
      } else if (side === 1) {    // top
        x = (Math.random() * 2 - 1) * a * 0.85; y = 1 - SEED_INSET; angle = -Math.PI / 2;
      } else if (side === 2) {    // left
        x = -a + SEED_INSET; y = (Math.random() * 2 - 1) * 0.85; angle = 0;
      } else {                    // right
        x = a - SEED_INSET; y = (Math.random() * 2 - 1) * 0.85; angle = Math.PI;
      }
      angle += (Math.random() * 2 - 1) * 0.5;
      const len = SEED_LENGTH * this._lengthScale;
      this._addSegment(x, y, x + Math.cos(angle) * len, y + Math.sin(angle) * len, 0);
    }
  }

  _addSegment(x0, y0, x1, y1, generation) {
    if (this._count >= this._maxSegments) return -1;
    const i = this._count++;
    const o = i * SEG_STRIDE;
    this._seg[o] = x0; this._seg[o + 1] = y0;
    this._seg[o + 2] = x1; this._seg[o + 3] = y1;
    this._seg[o + 4] = this._time;
    this._seg[o + 5] = generation;
    this._seg[o + 6] = Math.random();
    this._childCount[i] = 0;
    if (generation > this._maxGeneration) this._maxGeneration = generation;
    this._gridInsert(i, x0, y0, x1, y1);

    const v = i * 4;
    for (let k = 0; k < 4; k++) {
      this._aP0[(v + k) * 2] = x0; this._aP0[(v + k) * 2 + 1] = y0;
      this._aP1[(v + k) * 2] = x1; this._aP1[(v + k) * 2 + 1] = y1;
      this._aMeta[(v + k) * 2] = generation;
      this._aMeta[(v + k) * 2 + 1] = this._time;
    }
    this._attrP0.addUpdateRange(v * 2, 8); this._attrP0.needsUpdate = true;
    this._attrP1.addUpdateRange(v * 2, 8); this._attrP1.needsUpdate = true;
    this._attrMeta.addUpdateRange(v * 2, 8); this._attrMeta.needsUpdate = true;
    this._geometry.setDrawRange(0, this._count * 6);
    return i;
  }

  // Inside the cover's rectangle nothing may grow at all - "la pochette reste
  // dans une clairiere". Tested on the candidate TIP, in the same aspect-
  // corrected units the safe-zone uniforms are expressed in.
  // R19: the clearing is the centred cover block (stage), not the cover's
  // actual position.
  _inSafeZone(x, y) {
    const c = safeZoneUniforms.uStageCenter.value;
    const h = safeZoneUniforms.uStageHalfSize.value;
    return Math.abs(x - c.x) < h.x && Math.abs(y - c.y) < h.y;
  }

  _snapHex(angle) {
    if (this._hexOff) return angle;
    const snapped = Math.round(angle / HEX_STEP_RAD) * HEX_STEP_RAD;
    return angle + (snapped - angle) * HEX_SNAP;
  }

  // One growth event: take a terminal segment in the requested generation band
  // and give it one or two children.
  _grow(wantHighGen) {
    if (this._count >= this._maxSegments) return false;

    let n = 0;
    const cand = this._candidates;
    for (let i = 0; i < this._count; i++) {
      if (this._childCount[i] !== 0) continue;
      const gen = this._seg[i * SEG_STRIDE + 5];
      if (wantHighGen ? gen >= HIGH_GEN_MIN : gen <= LOW_GEN_MAX) cand[n++] = i;
    }
    // Nothing in that band yet (early on there are no deep generations at all):
    // fall back to any terminal rather than skipping the event, so the low band
    // still builds the trunk that the high band will later branch from.
    if (n === 0) {
      for (let i = 0; i < this._count; i++) if (this._childCount[i] === 0) cand[n++] = i;
    }
    if (n === 0) return false;

    const parent = cand[(Math.random() * n) | 0];
    const o = parent * SEG_STRIDE;
    const px = this._seg[o + 2], py = this._seg[o + 3];
    const parentAngle = Math.atan2(this._seg[o + 3] - this._seg[o + 1], this._seg[o + 2] - this._seg[o]);
    const parentLen = Math.hypot(this._seg[o + 2] - this._seg[o], this._seg[o + 3] - this._seg[o + 1]);
    const gen = this._seg[o + 5] + 1;
    const len = Math.max(LENGTH_FLOOR, parentLen * LENGTH_DECAY);

    const split = Math.random() < this._bifurcationP;
    const spread = ANGLE_SPREAD_DEG * Math.PI / 180;
    const minSpread = BIFURCATION_MIN_DEG * Math.PI / 180;

    let added = 0;
    const offsets = split
      ? [minSpread + Math.random() * (spread - minSpread), -(minSpread + Math.random() * (spread - minSpread))]
      : [(Math.random() * 2 - 1) * spread];

    for (let k = 0; k < offsets.length; k++) {
      const angle = this._snapHex(parentAngle + offsets[k]);
      const tx = px + Math.cos(angle) * len;
      const ty = py + Math.sin(angle) * len;
      if (tx < -this._aspect || tx > this._aspect || ty < -1 || ty > 1) { this._rejections++; continue; }
      if (this._inSafeZone(tx, ty)) { this._rejections++; continue; }
      // "moins d'une demi-longueur d'un segment existant" - the parent and the
      // sibling just added are exempt, they are supposed to touch.
      if (this._rejects(px, py, tx, ty, len * 0.5, parent, added > 0 ? this._count - 1 : -1)) { this._rejections++; continue; }
      if (this._addSegment(px, py, tx, ty, gen) === -1) break;
      added++;
    }
    if (added > 0) this._childCount[parent] = added;
    return added > 0;
  }

  _event(count, wantHighGen) {
    // Counted PER GROWTH, not per event: the acceptance criterion is about the
    // cost of one crossing test sweep, and an event runs up to four of them.
    this._testsLastEvent = 0;
    let growths = 0;
    for (let i = 0; i < count; i++) { this._grow(wantHighGen); growths++; }
    this._testsPerGrowth = growths > 0 ? this._testsLastEvent / growths : 0;
    this._eventCount++;
  }

  // --- palette --------------------------------------------------------------
  _applyPalette(palette) {
    const dominant = palette && palette.dominantColors;
    this._colorTo.set(ICE_COLOR);
    if (dominant && dominant.length > 0) {
      this._colorTo.lerp(this._scratchColor.set(dominant[0]), PALETTE_TINT);
    }
    capLuminance(this._colorTo);
  }

  _writeColor() {
    const t = clamp01((this._time * 1000 - this._paletteLerpStart) / PALETTE_LERP_MS);
    this._shared.uColor.value.copy(this._colorFrom).lerp(this._colorTo, t);
  }

  _maybeAdoptPalette(profile) {
    if (profile.artworkPalette === this._lastPaletteRef) return;
    this._lastPaletteRef = profile.artworkPalette;
    this._colorFrom.copy(this._shared.uColor.value);
    this._applyPalette(profile.artworkPalette);
    this._paletteLerpStart = this._time * 1000;
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    // "gel de la croissance, y compris l'animation des segments en cours
    // d'apparition" - the clock IS the animation, so holding it holds both.
    if (paused) return;

    this._time += deltaTime;
    this._maybeAdoptPalette(profile);
    this._writeColor();

    // AdaptivePartyVisualizer does not forward onTrackChange to its active
    // sub-visualizer (the finding sumi/halftone/loom all record), so the track
    // change is polled here.
    if (profile.trackId !== this._lastTrackId) {
      const first = this._lastTrackId === undefined;
      this._lastTrackId = profile.trackId;
      if (!first && this._count > 0) {
        this._fading = true;
        this._fadeStart = this._time;
      }
    }

    if (this._fading) {
      const t = clamp01((this._time - this._fadeStart) / TRACK_FADE_S);
      this._shared.uFade.value = 1 - t;
      if (t >= 1) {
        this._fading = false;
        this._shared.uFade.value = 1;
        this._plantSeeds();
      }
    }

    const director = profile.director;
    const bands = director && director.bands;
    const character = director && director.character;
    const live = !!(director && director.hasReliableGrid && bands);

    const dynamics = character && character.dynamics != null ? character.dynamics : 0.5;
    this._lengthScale = lerp(DYNAMICS_LENGTH_MIN, DYNAMICS_LENGTH_MAX, clamp01(dynamics));
    const progress = profile.duration > 0 ? clamp01(profile.progress) : 0.5;
    this._bifurcationP = lerp(BIFURCATION_P_START, BIFURCATION_P_END, progress);

    if (!this._fading && this._count < this._maxSegments) {
      if (this._forceGrow) {
        if (this._time * 1000 - this._lastEventTime >= 100) {
          this._lastEventTime = this._time * 1000;
          this._event(2, Math.random() < 0.5);
        }
      } else if (live) {
        const low = clamp01(bands.low), mid = clamp01(bands.mid), high = clamp01(bands.high);
        const rise = Math.max(0, mid - this._prevBands.mid);
        const riseLow = Math.max(0, low - this._prevBands.low);
        const riseHigh = Math.max(0, high - this._prevBands.high);
        this._prevBands.low = low; this._prevBands.mid = mid; this._prevBands.high = high;
        if (rise > RISE_THRESHOLD && this._time * 1000 - this._lastEventTime >= MIN_EVENT_MS) {
          this._lastEventTime = this._time * 1000;
          const n = 1 + Math.min(MAX_GROWTHS_PER_EVENT - 1, Math.floor(rise / RISE_THRESHOLD));
          // Which band rose harder decides which part of the structure this
          // event reaches: bass thickens the frame, treble extends the lace.
          this._event(n, riseHigh > riseLow);
        }
      } else {
        // No analysis: a slow random clock, alternating the two bands. The
        // brief is right that the result is nearly identical - almost nothing
        // here is keyed to an event, only to a rate.
        if (this._time * 1000 >= this._nextFallbackAt) {
          this._nextFallbackAt = this._time * 1000
            + FALLBACK_MIN_MS + Math.random() * (FALLBACK_MAX_MS - FALLBACK_MIN_MS);
          this._fallbackBandHigh = !this._fallbackBandHigh;
          this._event(FALLBACK_GROWTHS, this._fallbackBandHigh);
        }
      }
    }

    this._shared.uTime.value = this._time;
    const dpr = Math.min(typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1, 2);
    const h = (typeof window !== 'undefined' ? window.innerHeight : 1080) * dpr;
    if (h !== this._pixelHeight) {
      this._pixelHeight = h;
      this._shared.uPixelH.value = h;
    }
    const aspect = safeZoneUniforms.uAspect.value;
    if (aspect && aspect !== this._aspect) {
      this._aspect = aspect;
      this._shared.uAspect.value = aspect;
    }

    if (this._info && this._time - this._lastInfoAt >= 1) {
      const dt = this._time - this._eventWindowStart;
      this._eventsPerSecond = dt > 0 ? this._eventCount / dt : 0;
      this._eventCount = 0;
      this._eventWindowStart = this._time;
      this._lastInfoAt = this._time;
      // eslint-disable-next-line no-console
      console.log('[frost] segments=' + this._count + '/' + this._maxSegments
        + ' maxGen=' + this._maxGeneration
        + ' events/s=' + this._eventsPerSecond.toFixed(2)
        + ' crossingRejections=' + this._rejections
        + ' testsPerGrowth=' + this._testsPerGrowth.toFixed(1));
    }
  }

  dispose() {
    this._scene.remove(this._bg);
    this._bgGeometry.dispose();
    this._bgMaterial.dispose();
    if (this._halo) {
      this._scene.remove(this._halo);
      this._haloMaterial.dispose();
    }
    this._scene.remove(this._core);
    this._coreMaterial.dispose();
    this._geometry.dispose();
  }
}
